import { useParams, Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, XCircle, ChevronLeft, Info } from 'lucide-react';

import { useStressReport } from './api';
import type {
  StressLcrNsfrSection,
  StressNimSection,
  StressEveSection,
  StressShockParameters,
  StressComplianceVerdict,
} from './types';

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtRatio(v?: string | null, decimals = 2): string {
  if (v == null) return '—';
  const n = parseFloat(v);
  return isNaN(n) ? v : `${(n * 100).toFixed(decimals)} %`;
}

function fmtDelta(v?: string | null, decimals = 2): string {
  if (v == null) return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  const pct = (n * 100).toFixed(decimals);
  return n >= 0 ? `+${pct} pp` : `${pct} pp`;
}

function fmtAmount(v?: string | null, decimals = 0): string {
  if (v == null) return '—';
  const n = parseFloat(v);
  return isNaN(n) ? v : n.toLocaleString('fr-MA', { maximumFractionDigits: decimals });
}

function fmtBp(v?: number | null): string {
  if (v == null) return 'tenant default';
  return `${v > 0 ? '+' : ''}${v} bp`;
}

function fmtPct(v?: string | null): string {
  if (v == null) return 'tenant default';
  const n = parseFloat(v);
  return isNaN(n) ? v : `${(n * 100).toFixed(1)} %`;
}

// ── Compliance badge ──────────────────────────────────────────────────────────

function ComplianceBadge({ ok }: { ok?: boolean | null }) {
  if (ok == null) return <span className="text-gray-400 text-xs">unknown</span>;
  return ok ? (
    <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
      <CheckCircle2 size={13} /> Compliant
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
      <XCircle size={13} /> Breach
    </span>
  );
}

// ── Section card wrapper ──────────────────────────────────────────────────────

function SectionCard({ title, children, missing }: {
  title: string;
  children?: React.ReactNode;
  missing?: boolean;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
        {title}
      </h3>
      {missing ? (
        <p className="text-sm text-gray-400 italic">No data available for this section.</p>
      ) : children}
    </div>
  );
}

// ── Comparison row ────────────────────────────────────────────────────────────

function CompRow({ label, base, stressed, delta, compliance }: {
  label: string;
  base: string;
  stressed: string;
  delta?: string;
  compliance?: React.ReactNode;
}) {
  return (
    <tr className="border-t border-gray-100 text-sm">
      <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">{label}</td>
      <td className="py-2 px-3 text-right font-mono text-gray-700">{base}</td>
      <td className="py-2 px-3 text-right font-mono font-semibold text-gray-900">{stressed}</td>
      <td className="py-2 px-3 text-right font-mono text-gray-500">{delta ?? '—'}</td>
      {compliance !== undefined && (
        <td className="py-2 pl-3 text-right">{compliance}</td>
      )}
    </tr>
  );
}

// ── LCR / NSFR Section ────────────────────────────────────────────────────────

function LcrNsfrCard({ s }: { s: StressLcrNsfrSection }) {
  return (
    <SectionCard title="LCR / NSFR">
      <table className="w-full">
        <thead>
          <tr className="text-xs text-gray-400 uppercase">
            <th className="pb-2 text-left font-medium">Metric</th>
            <th className="pb-2 text-right font-medium">Base</th>
            <th className="pb-2 text-right font-medium">Stressed</th>
            <th className="pb-2 text-right font-medium">Δ</th>
            <th className="pb-2 text-right font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          <CompRow
            label="LCR Ratio"
            base={fmtRatio(s.baseLcrRatio)}
            stressed={fmtRatio(s.stressedLcrRatio)}
            delta={fmtDelta(s.lcrDelta)}
            compliance={<ComplianceBadge ok={s.stressedLcrCompliant} />}
          />
          <CompRow
            label="NSFR Ratio"
            base={fmtRatio(s.baseNsfrRatio)}
            stressed={fmtRatio(s.stressedNsfrRatio)}
            delta={fmtDelta(s.nsfrDelta)}
            compliance={<ComplianceBadge ok={s.stressedNsfrCompliant} />}
          />
        </tbody>
      </table>
    </SectionCard>
  );
}

// ── NIM Section ───────────────────────────────────────────────────────────────

function NimCard({ s }: { s: StressNimSection }) {
  const deltaColor = s.nimImproves ? 'text-green-600' : 'text-red-600';
  return (
    <SectionCard title="Net Interest Margin (NIM)">
      <table className="w-full">
        <thead>
          <tr className="text-xs text-gray-400 uppercase">
            <th className="pb-2 text-left font-medium">Metric</th>
            <th className="pb-2 text-right font-medium">Base</th>
            <th className="pb-2 text-right font-medium">Stressed</th>
            <th className="pb-2 text-right font-medium">Δ</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-gray-100 text-sm">
            <td className="py-2 pr-4 text-gray-500">NIM</td>
            <td className="py-2 px-3 text-right font-mono text-gray-700">
              {fmtAmount(s.baseNimBaseline)}
            </td>
            <td className="py-2 px-3 text-right font-mono font-semibold text-gray-900">
              {fmtAmount(s.stressedNim)}
            </td>
            <td className={`py-2 px-3 text-right font-mono font-semibold ${deltaColor}`}>
              {fmtAmount(s.nimDelta)}
              <span className="ml-1 text-xs font-normal">
                ({s.nimImproves ? 'improves' : 'declines'})
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </SectionCard>
  );
}

// ── EVE Section ───────────────────────────────────────────────────────────────

function EveCard({ s }: { s: StressEveSection }) {
  return (
    <SectionCard title="EVE (Worst-Case)">
      <table className="w-full mb-3">
        <thead>
          <tr className="text-xs text-gray-400 uppercase">
            <th className="pb-2 text-left font-medium">Metric</th>
            <th className="pb-2 text-right font-medium">Base</th>
            <th className="pb-2 text-right font-medium">Stressed</th>
            <th className="pb-2 text-right font-medium">Δ</th>
          </tr>
        </thead>
        <tbody>
          <CompRow
            label="EVE Worst-Case"
            base={fmtAmount(s.baseEveWorstCase)}
            stressed={fmtAmount(s.stressedEveWorstCase)}
            delta={fmtAmount(s.eveDelta)}
          />
        </tbody>
      </table>
      {s.note && (
        <p className="text-xs text-gray-400 flex items-start gap-1.5">
          <Info size={12} className="mt-0.5 shrink-0" />
          {s.note}
        </p>
      )}
    </SectionCard>
  );
}

// ── Shock Parameters ──────────────────────────────────────────────────────────

function ShockCard({ s }: { s: StressShockParameters }) {
  const rows: [string, string][] = [
    ['Rate shock (parallel)', fmtBp(s.rateShockBp)],
    ['NIM shock', fmtBp(s.nimShockBp)],
    ['LCR retail stable run-off', fmtPct(s.lcrRunoffRetailStable)],
    ['LCR retail less-stable run-off', fmtPct(s.lcrRunoffRetailLessStable)],
    ['LCR wholesale operational run-off', fmtPct(s.lcrRunoffWholesaleOperational)],
    ['LCR wholesale non-operational run-off', fmtPct(s.lcrRunoffWholesaleNonOperational)],
    ['HQLA Level 2A haircut', fmtPct(s.lcrHqlaLevel2aHaircut)],
    ['HQLA Level 2B haircut', fmtPct(s.lcrHqlaLevel2bHaircut)],
  ];
  return (
    <SectionCard title="Shock Parameters">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm col-span-1">
            <dt className="text-gray-500 mr-2 truncate">{k}</dt>
            <dd className="font-mono text-gray-800 shrink-0">{v}</dd>
          </div>
        ))}
      </dl>
    </SectionCard>
  );
}

// ── Compliance Verdict ────────────────────────────────────────────────────────

function VerdictCard({ v }: { v: StressComplianceVerdict }) {
  const isCompliant = v.overallCompliant === true;
  const isBreached  = v.overallCompliant === false;
  const bgClass     = isCompliant
    ? 'bg-green-50 border-green-200'
    : isBreached
    ? 'bg-red-50 border-red-200'
    : 'bg-gray-50 border-gray-200';
  const iconClass   = isCompliant ? 'text-green-500' : isBreached ? 'text-red-500' : 'text-gray-400';
  const Icon        = isCompliant ? CheckCircle2 : isBreached ? XCircle : Info;

  return (
    <div className={`border rounded-lg p-5 mb-4 ${bgClass}`}>
      <div className="flex items-start gap-3">
        <Icon size={20} className={`mt-0.5 shrink-0 ${iconClass}`} />
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-1">Supervisory Assessment</h3>
          {v.assessmentText && (
            <p className="text-sm text-gray-700 mb-2">{v.assessmentText}</p>
          )}
          {v.lcrBreachNote && (
            <p className="text-xs text-red-700 mt-1">⚠ {v.lcrBreachNote}</p>
          )}
          {v.nsfrBreachNote && (
            <p className="text-xs text-red-700 mt-1">⚠ {v.nsfrBreachNote}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function StressReportPage() {
  const { runId } = useParams<{ runId: string }>();
  const { data: report, isLoading, isError, error } = useStressReport(runId);

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-400 text-sm">
        Loading stress report…
      </div>
    );
  }

  if (isError || !report) {
    return (
      <div className="p-8">
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 max-w-lg">
          <AlertTriangle size={18} className="text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Report unavailable</p>
            <p className="text-xs text-red-600 mt-1">
              {(error as { message?: string })?.message ?? 'Run not found or data unavailable.'}
            </p>
          </div>
        </div>
        <Link
          to="/alm/stress"
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
        >
          <ChevronLeft size={14} /> Back to Stress Tests
        </Link>
      </div>
    );
  }

  const { metadata: meta, shockParameters, lcrNsfrSection, nimSection, eveSection, complianceVerdict } = report;

  return (
    <div className="max-w-4xl mx-auto px-6 py-6">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <Link
            to="/alm/stress"
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 mb-2"
          >
            <ChevronLeft size={13} /> Stress Tests
          </Link>
          <h1 className="text-xl font-semibold text-gray-900">{meta.scenarioName}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Stress Report — Run <span className="font-mono text-xs">{meta.runId.slice(0, 8)}…</span>
          </p>
        </div>
        <div className="text-right text-xs text-gray-400 space-y-0.5">
          <p>Calculation date: <strong className="text-gray-600">{meta.calculationDate}</strong></p>
          <p>Generated: {new Date(meta.generatedAt).toLocaleString('fr-MA')}</p>
          <p>Engine: {meta.engineVersion}</p>
          {meta.triggeredBy && <p>Triggered by: {meta.triggeredBy}</p>}
          {meta.partial && (
            <span className="inline-block mt-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
              ⚠ Partial report
            </span>
          )}
        </div>
      </div>

      {/* ── Compliance Verdict ── */}
      {complianceVerdict && <VerdictCard v={complianceVerdict} />}

      {/* ── Metrics ── */}
      {lcrNsfrSection ? (
        <LcrNsfrCard s={lcrNsfrSection} />
      ) : (
        <SectionCard title="LCR / NSFR" missing />
      )}

      {nimSection ? (
        <NimCard s={nimSection} />
      ) : (
        <SectionCard title="Net Interest Margin (NIM)" missing />
      )}

      {eveSection ? (
        <EveCard s={eveSection} />
      ) : (
        <SectionCard title="EVE (Worst-Case)" missing />
      )}

      {/* ── Shock Parameters ── */}
      {shockParameters && <ShockCard s={shockParameters} />}

      {/* ── Footer metadata ── */}
      <p className="text-xs text-gray-300 mt-6 text-center">
        Report ID {meta.reportId} · Scenario {meta.scenarioId} · Type {meta.scenarioType}
      </p>

    </div>
  );
}
